import axios, { AxiosError, AxiosResponse } from 'axios';
import { ErrorDetails } from '@morf/server/types';
import { GetAppointmentTypeInfoRequestData } from '../../pages/api/getAppointmentTypeInfo';
import { GetProviderRequestData } from '../../pages/api/getProvider';
import { MorfAppointment } from './formatScheduleAppointmentResponseToMorfAppointment';
import { RescheduleAppointmentRequestData } from '../../pages/api/rescheduleAppointment';
import { RescheduleMorfAppointment } from './getFormattedRescheduledAppointment';
import { ScheduleAppointmentRequestData } from '../../pages/api/scheduleAppointment';
import { TrackTimeSlotSelectedRequestData } from '../../pages/api/trackTimeSlotSelected';
import { buildPostMessagePayload } from './buildPostMessagePayload';
import {
  AvailabilitySlot,
  ReactNativeWebView,
} from '../shared/AvailabilitySlots/AvailabilitySlot/types';
import { formatTimestampToMoment } from './formatTimestampToMoment';
import { generateQueryParams } from './generateQueryParams';
import { getEventData } from './getEventData';
import { getViewTypeFromPath } from './getViewTypeFromPath';
import { morf_analytics } from '@morf/proto/morf_analytics/v1/morf_analytics_ts_proto';
import { scheduling } from '@morf/proto/scheduling/v1/scheduling_ts_proto';
import { setAnswerValue } from '@formsort/custom-question-api';
import { useFormsortAnswers } from './useFormsortAnswers';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useSchedule } from '../context/schedule/useSchedule';
import { useRef, useState } from 'react';
import { useUrlParams } from './useUrlParams';

export type UseConfirmTimeSlotResult = {
  /**
   * True from the moment a confirm is accepted until it fails; stays
   * true after success while navigating away. Drives the button's
   * disabled state to prevent duplicate bookings.
   */
  isConfirming: boolean;
  /**
   * Track the time slot selection and trigger the appropriate booking
   * mutation (reschedule when `appointmentId` is present, otherwise
   * schedule). Behaviour mirrors AvailabilitySlot.handleConfirmSlot 1:1
   * so the v1 desktop view and the v2 mobile card share booking logic.
   */
  handleConfirmSlot: () => Promise<void>;
};

/**
 * Encapsulates the per-slot booking pipeline used by both
 * `AvailabilitySlot` (desktop) and `BasicViewV2` (mobile card):
 *
 * 1. Lazy-load the provider + appointment-type info for this slot.
 * 2. On confirm, POST `/api/trackTimeSlotSelected` for analytics.
 * 3. Dispatch to `handleScheduleAppointment` or
 *    `handleRescheduleAppointment` depending on whether
 *    `appointmentId` is in the URL params.
 * 4. Either postMessage / Formsort-answer-set inside an iframe, or
 *    full HTTP POST + navigate to `/confirmation` | `/error-details`.
 */
export const useConfirmTimeSlot = (
  availabilitySlot: AvailabilitySlot
): UseConfirmTimeSlotResult => {
  const router = useRouter();
  const { isFormsortFlow } = useFormsortAnswers();

  const urlParams = useUrlParams();
  const {
    appointmentId,
    appointmentTypeId,
    emailAddress,
    firstName,
    lastName,
    morfOrgId,
    patientId,
    schedulingApplication,
    submissionMethod,
    locationId,
    contactType,
  } = urlParams;

  const { schedulingTimezone } = useSchedule();

  const providerId = availabilitySlot.providerId?.id;

  const { data: provider } = useQuery<
    scheduling.v1.Provider,
    AxiosError<ErrorDetails>
  >(
    [
      '/scheduling.v1.SchedulingService/GetProvider',
      morfOrgId,
      patientId,
      providerId,
      schedulingApplication,
    ],
    async () => {
      const requestData: GetProviderRequestData = {
        morfOrgId,
        patientId,
        providerId,
        schedulingApplication,
      };
      const response = await axios.post('/api/getProvider', requestData);
      return response.data;
    }
  );

  const { data: appointmentTypeInfo } = useQuery<
    scheduling.v1.AppointmentTypeInfo,
    AxiosError<ErrorDetails>
  >(
    [
      '/scheduling.v1.SchedulingService/GetAppointmentTypeInfo',
      appointmentTypeId,
      morfOrgId,
      patientId,
      schedulingApplication,
    ],
    async () => {
      const requestData: GetAppointmentTypeInfoRequestData = {
        appointmentTypeId,
        morfOrgId,
        patientId,
        schedulingApplication,
      };
      const response = await axios.post(
        '/api/getAppointmentTypeInfo',
        requestData
      );
      return response.data;
    }
  );

  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  // Synchronous re-entry lock: React state updates are async, so rapid
  // repeat clicks can all read isConfirming === false before the first
  // render disables the button. The ref blocks those immediately.
  const isConfirmingRef = useRef(false);

  const { mutateAsync: handleRescheduleAppointment } = useMutation(async () => {
    const datetime = formatTimestampToMoment(
      availabilitySlot.slot
    ).toISOString();

    const requestData: RescheduleAppointmentRequestData = {
      existingAppointmentId: appointmentId,
      morfOrgId,
      newAppointmentDatetime: datetime,
      patientId,
      providerId: availabilitySlot.providerId?.id,
      schedulingApplication,
      schedulingTimezone,
    };

    if (submissionMethod === 'postMessage') {
      window.parent.postMessage(
        buildPostMessagePayload(
          'reschedule_appointment_request_data',
          requestData
        ),
        '*'
      );
    } else {
      const response: AxiosResponse<RescheduleMorfAppointment | ErrorDetails> =
        await axios.post<RescheduleMorfAppointment | ErrorDetails>(
          `/api/rescheduleAppointment`,
          requestData
        );

      if (isFormsortFlow) {
        // Set the answer value after the call to rescheduleAppointment to
        // maintain compatibility with existing customers who use
        // RescheduleMorfAppointment for tracking.
        setAnswerValue(response.data);
      }

      if ('message' in response.data) {
        const params = {
          message: response.data.message,
          morfOrgId,
        };

        const queryString = generateQueryParams(params);
        router.push(`/error-details?${queryString}`);
      } else {
        const params = {
          appointmentId: response.data.newAppointment.id,
          morfOrgId,
          patientId,
          returnUrl: router.asPath,
          schedulingApplication,
        };

        const queryString = generateQueryParams(params);
        router.push(`/confirmation?${queryString}`);
      }
    }
  });

  const { mutateAsync: handleScheduleAppointment } = useMutation(async () => {
    const datetime = formatTimestampToMoment(
      availabilitySlot.slot
    ).toISOString();

    const timeSlot = {
      appointmentTypeId,
      datetime,
      providerId,
      providerName: provider?.name,
      timezone: schedulingTimezone,
    };

    const isBrowser = typeof window !== 'undefined';
    const isWithinIframe =
      isBrowser && window.location !== window.parent.location;

    if (isWithinIframe) {
      if (isFormsortFlow) {
        setAnswerValue(timeSlot);
      } else {
        window.parent.postMessage(
          buildPostMessagePayload('time_slot', timeSlot),
          '*'
        );
      }

      if ('ReactNativeWebView' in window) {
        const reactNativeWebView =
          window.ReactNativeWebView as ReactNativeWebView;
        reactNativeWebView.postMessage(JSON.stringify(timeSlot));
      }
    } else {
      const requestData: ScheduleAppointmentRequestData = {
        appointmentTypeId,
        datetime,
        emailAddress,
        firstName,
        lastName,
        morfOrgId,
        patientId,
        providerId,
        schedulingApplication,
        timezone: schedulingTimezone,
        locationId,
        contactType,
      };

      const response: AxiosResponse<MorfAppointment | ErrorDetails> =
        await axios.post<MorfAppointment | ErrorDetails>(
          '/api/scheduleAppointment',
          requestData
        );

      if ('message' in response.data) {
        const params = {
          message: response.data.message,
          morfOrgId,
        };

        const queryString = generateQueryParams(params);
        router.push(`/error-details?${queryString}`);
      } else {
        const params = {
          appointmentId: response.data.id,
          morfOrgId,
          patientId,
          returnUrl: router.asPath,
          schedulingApplication,
        };

        const queryString = generateQueryParams(params);
        router.push(`/confirmation?${queryString}`);
      }
    }
  });

  const handleConfirmSlot = async () => {
    if (isConfirmingRef.current) {
      return;
    }
    isConfirmingRef.current = true;
    setIsConfirming(true);

    if (appointmentTypeInfo && provider) {
      const eventData = getEventData(router.query, urlParams);
      const requestData: TrackTimeSlotSelectedRequestData = {
        appointmentType: appointmentTypeInfo,
        datetime: availabilitySlot.slot,
        eventData,
        morfOrgId,
        patientId,
        providerScheduling: new morf_analytics.v1.SchedulingProvider({
          providerId: availabilitySlot.providerId,
          name: provider.name,
        }),
        schedulingApplication,
        viewType: getViewTypeFromPath(router.asPath),
      };
      // Fire-and-forget: analytics must never block or fail the booking.
      axios.post('/api/trackTimeSlotSelected', requestData).catch((error) => {
        console.error('Failed to track time slot selection', error);
      });
    }

    try {
      await (appointmentId
        ? handleRescheduleAppointment()
        : handleScheduleAppointment());
    } catch (error) {
      // Re-enable the button so the user can retry after a failure. On
      // success we stay locked while navigating to /confirmation or
      // handing off via postMessage.
      isConfirmingRef.current = false;
      setIsConfirming(false);
      throw error;
    }
  };

  return {
    isConfirming,
    handleConfirmSlot,
  };
};
